import { readCookie, writeCookie } from './lib/browser-cookies';
import { CONSENT_LIFETIME_MS, getParentCookieDomain } from './lib/consent-value';
import {
    BANNER_VARIANT_COOKIE,
    resolveBannerVariant,
    type BannerExperimentConfig,
    type BannerVariantResolution,
} from './lib/banner-variant';

/**
 * Browser-side banner variant assignment (SDUI-1604).
 *
 * Call immediately before injecting `uc.js`, and pass the returned `cookiebotId` to it rather
 * than the build-time or env one. Synchronous by design: the Gatsby site decides its domain
 * group during render, so anything asynchronous here would race the script it is choosing for.
 *
 * The variant is also written to `data-cookie-variant` on the document element, so the banner
 * stylesheets can scope their rules to one design. Several of those rules select by position
 * (`li.CookieCard:nth-child(3)` and friends) and would otherwise land on whatever occupies
 * that position in the other variant's DOM.
 */

/** Matches the consent lifetime: the assignment matters exactly as long as the decision does. */
const variantExpiresAt = (): Date => new Date(Date.now() + CONSENT_LIFETIME_MS);

const recordVariant = (resolution: BannerVariantResolution, hostname: string): void => {
    if (!resolution.minted) return;

    // Share across subdomains where there is a registered parent, so the assignment survives the
    // www to app hop. Host-only otherwise, which is the right scope for `localhost`: there are no
    // sibling hosts to reach, and refusing to write would re-flip the variant on every page load
    // and make the experiment untestable against the SSR dev server.
    const domain = getParentCookieDomain(hostname);

    // A failed write is not worth recovering from: the visitor is simply re-flipped on the next
    // page, which costs the experiment nothing because each decision is independently assigned.
    writeCookie(BANNER_VARIANT_COOKIE, resolution.minted, { domain, expires: variantExpiresAt() });
};

/**
 * Resolves the banner variant for this page and records a new assignment if there wasn't one.
 *
 * Returns the existing domain group unchanged when the experiment is off, when the surface is
 * not in it, or when there is no DOM, so every caller can use the result unconditionally.
 */
export const resolveBannerVariantInBrowser = (config: BannerExperimentConfig): BannerVariantResolution => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
        return { variant: 'control', cookiebotId: config.controlCookiebotId, minted: null };
    }

    const { hostname, pathname } = window.location;
    const resolution = resolveBannerVariant({
        config,
        hostname,
        pathname,
        recorded: readCookie(BANNER_VARIANT_COOKIE),
    });

    recordVariant(resolution, hostname);
    document.documentElement.setAttribute('data-cookie-variant', resolution.variant);

    return resolution;
};
